import React, { useMemo } from "react";
import { Formik, Form } from "formik";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import SchoolForm from "components/SchoolForm/SchoolForm";
import { PrimaryButton } from "widgets";
import { useCreateSchool } from "store/backend/apiHooks";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import Loader from "shared/Icons/Loader/Loader";
import { SCHOOL_API_RESPONSE_FIELDS, SCHOOL_ID_QUERY_PARAM } from "constants/schoolConstants";
import routes from "constants/routes";
import {
  buildSchoolPayload,
  canCreateSchool,
  createSchoolSchema,
  getSchoolApiErrorMessage,
  isCreateSchoolNameLocked,
  isCreateSchoolUdiseLocked,
  mergeCreateSchoolPrefill,
  useSchoolMappingLists,
} from "helpers/schoolUtils";

import styles from "../CreateTenant/CreateTenant.module.scss";

const CreateSchool = () => {
  const { openSnackbar } = useSnackbarContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { mutate: createSchool, isPending: isCreateSchoolLoading } =
    useCreateSchool();
  const mapping = useSchoolMappingLists();
  const validationSchema = useMemo(
    () => createSchoolSchema(mapping.countryOptions),
    [mapping.countryOptions],
  );
  const initialValues = useMemo(
    () => mergeCreateSchoolPrefill(location.state, mapping.countryOptions),
    [location.state, mapping.countryOptions],
  );
  const schoolNameDisabled = isCreateSchoolNameLocked(location.state);
  const udiseCodeDisabled = isCreateSchoolUdiseLocked(location.state);
  if (!canCreateSchool()) {
    return <Navigate to={routes.schools.schools} replace />;
  }
  return (
    <div className={styles.mainContainer}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <Link className={styles.backLink} to={routes.schools.schools}>
            ← Back to schools
          </Link>
          <h1 className={styles.pageTitle}>Create School</h1>
        </div>
      </div>
      <div className={styles.formWrap}>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          validateOnMount={false}
          validateOnChange={true}
          validateOnBlur={true}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            createSchool(buildSchoolPayload(values, mapping.countryOptions), {
              onSuccess: (response) => {
                setSubmitting(false);
                const schoolId = response?.data?.[SCHOOL_API_RESPONSE_FIELDS.SCHOOL_ID];
                navigate(
                  `${routes.schoolDetails.schoolDetails}?${SCHOOL_ID_QUERY_PARAM}=${schoolId}`,
                );
                openSnackbar("School created successfully!", "success");
              },
              onError: (error) => {
                setSubmitting(false);
                openSnackbar(
                  `Error creating school: ${getSchoolApiErrorMessage(error)}`,
                  "error",
                );
              },
            });
          }}
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            setFieldTouched,
            setFieldError,
            handleBlur,
            handleSubmit,
            isSubmitting,
            submitCount,
          }) => (
            <Form onSubmit={handleSubmit}>
              <SchoolForm
                mapping={mapping}
                values={values}
                touched={touched}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                setFieldError={setFieldError}
                handleBlur={handleBlur}
                errors={errors}
                submitCount={submitCount}
                schoolNameDisabled={schoolNameDisabled}
                udiseCodeDisabled={udiseCodeDisabled}
              />
              <div className={styles.submitRow}>
                <PrimaryButton
                  type="submit"
                  disabled={isSubmitting || isCreateSchoolLoading}
                >
                  {isSubmitting || isCreateSchoolLoading ? (
                    <Loader />
                  ) : (
                    "Create school"
                  )}
                </PrimaryButton>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default CreateSchool;
